import { push } from 'redux-first-history';
import { call, fork, put, select, take, takeEvery, takeLatest, cancelled } from 'redux-saga/effects';
import { sendLog } from '@siteground/styleguide';

import { VISITOR_AUTH_FAILURE, VISITOR_AUTH_SUCCESS } from '../../../../src/actions/auth';

import {
  CHAT_NOT_EXISTS,
  CONTINUE_CHAT,
  SEND_INITIAL_MESSAGES_TO_CLIENT,
  USER_TOGGLED_SOUND,
  USER_SUBMITTED_CHAT_RATING,
  SUPPORT_OPERATOR_IS_TYPING
} from '../../../../src/actions/chat';

import EXTERNAL_APP_EVENTS from '../../../../src/constants/external-app-events';
import * as WEBHOOK_EVENTS from '../../../../src/actions/webhooks';
import { CHAT_NOT_EXISTS_REASONS } from '../../../../src/shared/constants';
import { createSystemMessage, toggleChatSound } from '../../actions';
import { removeFileForUpload } from '../../actions/file-upload';
import { CHAT_STATE, FILE_UPLOAD_DISABLED_INTL_KEY, SYSTEM_MESSAGE_TYPES } from '../../constants';
import { CHAT_LAYOUT_DOM_ID } from '../../constants/dom-ids';
import { ROUTES } from '../../constants/routes';
import { didOperatorLostConnection, getChatState, isChatEndedByUser } from '../../selectors/chat';
import { getCurrentPathname } from '../../selectors/route';
import * as FromClientFEActions from '../actions/from-client-frontend';
import * as FromClusterActions from '../actions/from-cluster';
import * as FromMainAppActions from '../actions/from-main-app';
import { WEBSOCKET_CONNECTION_FAILED_EVENTS, RETRY_TIMEOUT_MS } from '../api/websocket';
import EVENTS from '../constants/events';
import { trigger } from '../events-manager';
import { closeChatWindowSaga, waitForChatRender } from './chat-window-helpers';
import { CLIENT_WEBSOCKET_AUTH_ERROR } from '../../../../src/actions/system-actions';
import { ERROR_TYPES as CLUSTER_CUSTOM_ERROR_TYPES } from '../../../../src/errors';
import { delay } from '../../utils/delay';
import { getVisitorToken } from '../../selectors/chat-session';
import { getDebugEvents } from '../../selectors/debug';

import { debugEvent } from '../../actions/debug';
import { parseJwt, getJwtBodyPart } from '../utils/jwt';

export function* visitorOwnsVisitorToken(visitorToken, when) {
  try {
    const { chatSession } = yield select();

    const { clientToken, unauthorized } = chatSession;

    if (unauthorized) {
      return true;
    }

    const visitorTokenData = parseJwt({ token: visitorToken });
    const clientTokenData = parseJwt({ token: clientToken });

    const hasProblem = Boolean(
      clientTokenData && visitorTokenData && String(visitorTokenData.user_id) !== String(clientTokenData.user_id)
    );

    if (hasProblem) {
      const debugEvents = yield select(getDebugEvents);

      sendLog({
        error: `Chat -> ${when} Visitor and client token doesnt match userId`,
        errorInfo: {
          componentStack: ''
        },
        data: JSON.stringify({
          agent: window.navigator.userAgent,
          clientTokenData,
          visitorTokenData,
          debugEvents
        })
      });

      return false;
    }
  } catch (e) {}

  return true;
}

export function* triggerWebsocketReconnect(channel, errReason) {
  const { chatSession } = yield select();

  channel && channel.close();

  const { IMPULSE_URLS, CHAT_API, clientToken, username, unauthorized } = chatSession;

  yield put(
    debugEvent({
      name: 'TRIGERED_WEBSOCKET_RECONNECT',
      payload: {
        reason: errReason,
        IMPULSE_URLS,
        CHAT_API,
        clientToken: getJwtBodyPart({ token: clientToken })
      }
    })
  );

  yield delay(RETRY_TIMEOUT_MS);

  if (unauthorized) {
    return yield put(FromClientFEActions.initWebsocketUnAuthorized());
  }

  // reconnect
  return yield put(
    FromClientFEActions.initWebsocket({
      IMPULSE_URLS,
      CHAT_API,
      clientToken,
      username,
      reconnect: true
    })
  );
}

export function* takeActionFromChannel(channel) {
  try {
    return yield take(channel);
  } finally {
    if (yield cancelled()) {
      channel.close();
    }
  }
}

export function* watchClusterEventsChannel({ clusterEventsChannel, connectedVisitorToken }) {
  /*
  Hook here all events coming as cluster events and dispatch them as
  FromClusterActions, so other sagas can take them and handle side effects */
  while (true) {
    const action = yield call(takeActionFromChannel, clusterEventsChannel);
    if (action && action.type === CLIENT_WEBSOCKET_AUTH_ERROR) {
      const error = action.payload && action.payload.error;
      yield put(
        debugEvent({
          name: 'AUTH_ERROR_FROM_CLUSTER',
          payload: {
            error
          }
        })
      );

      if (error === CLUSTER_CUSTOM_ERROR_TYPES.INVALID_VISITOR_TOKEN) {
        yield put(FromClientFEActions.storeVisitorToken({ visitorToken: null }));
        yield call(triggerWebsocketReconnect, clusterEventsChannel, CLUSTER_CUSTOM_ERROR_TYPES.INVALID_VISITOR_TOKEN);
        return;
      }

      yield put(
        debugEvent({
          name: 'REQUESTING_TO_UPDATE_CLIENT_TOKEN',
          payload: {
            error
          }
        })
      );

      trigger(EVENTS.FAILED_TO_AUTHENTICATE_WITH_CLIENT_TOKEN);

      yield take(FromMainAppActions.CLIENT_TOKEN_UPDATED);
      yield call(triggerWebsocketReconnect, clusterEventsChannel, EVENTS.FAILED_TO_AUTHENTICATE_WITH_CLIENT_TOKEN);
      return;
    }

    if (WEBSOCKET_CONNECTION_FAILED_EVENTS.includes(action)) {
      yield call(triggerWebsocketReconnect, clusterEventsChannel, action);
      return;
    }

    // console.log('[Client received message from cluster]', action);

    const clusterAction = action && action.clusterAction;
    const clusterActionType = clusterAction && clusterAction.type;

    if (!clusterActionType) {
      continue;
    }

    // TODO refactor to object and separate to semantic files
    switch (clusterActionType) {
      case VISITOR_AUTH_SUCCESS:
        const sessionId = clusterAction && clusterAction.payload && clusterAction.payload.sessionId;
        const visitorToken = clusterAction && clusterAction.payload && clusterAction.payload.visitorToken;

        const visitorOwnsToken = yield call(visitorOwnsVisitorToken, visitorToken, 'auth-success');

        if (!visitorOwnsToken) {
          yield put(
            debugEvent({
              name: 'VISITOR_AUTH_FAILURE (doesnt own the token)',
              payload: {
                error: 'Handled in FE'
              }
            })
          );

          yield put(FromClusterActions.visitorAuthFailed(clusterAction));
          return yield call(triggerWebsocketReconnect, clusterEventsChannel, 'VISITOR_DOESNT_OWN_HIS_TOKEN');
        }

        yield put(
          debugEvent({
            name: 'VISITOR_AUTH_SUCCESS (from cluster)',
            payload: {
              visitorToken: getJwtBodyPart({ token: visitorToken })
            }
          })
        );

        yield put(FromClusterActions.visitorAuthSuccess({ sessionId, visitorToken }));

        if (connectedVisitorToken !== visitorToken) {
          return yield call(triggerWebsocketReconnect, clusterEventsChannel, 'RECONNECT_AFTER_VISITOR_AUTH');
        }

        break;
      case VISITOR_AUTH_FAILURE:
        yield put(
          debugEvent({
            name: 'VISITOR_AUTH_FAILURE (from cluster)',
            payload: {
              error: clusterAction && clusterAction.payload && clusterAction.payload.error
            }
          })
        );

        yield put(FromClusterActions.visitorAuthFailed(clusterAction));
        break;

      case EXTERNAL_APP_EVENTS.VISITOR_SEND_MESSAGE:
        yield put(FromClusterActions.visitorSendMessage(clusterAction.payload));
        break;

      //  Chat events
      case CHAT_NOT_EXISTS:
        yield put(
          debugEvent({
            name: 'CHAT_NOT_EXISTS (from cluster)',
            payload: {
              ...clusterAction.payload
            }
          })
        );

        yield put(FromClusterActions.chatNotExists({ ...clusterAction.payload }));

        return yield call(triggerWebsocketReconnect, clusterEventsChannel, 'RECONNECT_AFTER_CHAT_NOT_EXISTS');
      case CONTINUE_CHAT:
        const vTokenContinueChat = yield select(getVisitorToken);
        yield call(visitorOwnsVisitorToken, vTokenContinueChat, 'continue_chat');
        yield put(FromClusterActions.continueChat({ ...clusterAction.payload }));
        break;
      case EXTERNAL_APP_EVENTS.CHAT_ASSIGNED:
        yield put(FromClusterActions.chatAssigned({ ...clusterAction.payload }));
        break;
      case SEND_INITIAL_MESSAGES_TO_CLIENT:
        const vTokenSendInitial = yield select(getVisitorToken);
        yield call(visitorOwnsVisitorToken, vTokenSendInitial, 'send_messages_initial');
        yield put(FromClusterActions.sendInitialChatMessagesToClient({ ...clusterAction.payload }));
        break;
      case EXTERNAL_APP_EVENTS.CHAT_CLOSED_BY_OPERATOR:
        yield put(
          createSystemMessage({
            type: SYSTEM_MESSAGE_TYPES.INSTRUCTION,
            intlKey: 'translate.system.message.chat.session.ended'
          })
        );
        yield put(FromClusterActions.chatClosedByOperator());
        break;
      case EXTERNAL_APP_EVENTS.CHAT_CLOSED_BY_ADMIN:
        yield put(
          createSystemMessage({
            type: SYSTEM_MESSAGE_TYPES.INSTRUCTION,
            intlKey: 'translate.system.message.chat.session.ended'
          })
        );
        yield put(FromClusterActions.chatClosedByAdmin());
        break;
      case USER_TOGGLED_SOUND:
        const isSoundOn = clusterAction && clusterAction.payload && clusterAction.payload.isSoundOn;
        yield put(toggleChatSound({ isSoundOn, isDistributed: true }));
        break;
      case USER_SUBMITTED_CHAT_RATING:
        yield put(FromClusterActions.userSubmittedChatRating());
        break;
      //  Operator actions
      case EXTERNAL_APP_EVENTS.OPERATOR_SEND_MESSAGE:
        yield put(FromClusterActions.operatorSendMessage(clusterAction.payload));
        break;
      case SUPPORT_OPERATOR_IS_TYPING:
        yield put(FromClusterActions.operatorIsTyping(clusterAction.payload));
        break;
      case EXTERNAL_APP_EVENTS.OPERATOR_TRANSLATE_MESSAGE:
        yield put(FromClusterActions.operatorSendTranslatedMessage(clusterAction.payload));
        break;
      case EXTERNAL_APP_EVENTS.OPERATOR_LOST_CONNECTION:
        yield put(
          createSystemMessage({
            type: SYSTEM_MESSAGE_TYPES.INSTRUCTION,
            intlKey: 'translate.system.message.operator.lost.connection'
          })
        );
        break;

      //  File Upload
      case EXTERNAL_APP_EVENTS.ENABLE_FILE_UPLOAD:
        yield put(FromClientFEActions.setChatStatus(CHAT_STATE.FILE_UPLOAD_ENABLED));
        yield put(
          createSystemMessage({
            type: SYSTEM_MESSAGE_TYPES.INSTRUCTION,
            intlKey: 'translate.system.message.file.upload.enabled'
          })
        );
        break;

      case EXTERNAL_APP_EVENTS.DISABLE_FILE_UPLOAD:
        yield all([put(FromClientFEActions.setChatStatus(CHAT_STATE.IN_PROGRESS)), put(removeFileForUpload())]);
        yield put(
          createSystemMessage({
            type: SYSTEM_MESSAGE_TYPES.INSTRUCTION,
            intlKey: FILE_UPLOAD_DISABLED_INTL_KEY
          })
        );
        break;

      //  Chat Offload
      case EXTERNAL_APP_EVENTS.OFFLOAD_START:
        yield put(
          createSystemMessage({
            type: SYSTEM_MESSAGE_TYPES.INSTRUCTION,
            intlKey: 'translate.system.message.chat.offload.start'
          })
        );
        break;
      case EXTERNAL_APP_EVENTS.OFFLOAD_DECLINED:
        yield put(
          createSystemMessage({
            type: SYSTEM_MESSAGE_TYPES.INSTRUCTION,
            intlKey: 'translate.system.message.chat.offload.declined'
          })
        );
        break;
      case EXTERNAL_APP_EVENTS.OFFLOAD_FAILED:
        yield put(
          createSystemMessage({
            type: SYSTEM_MESSAGE_TYPES.INSTRUCTION,
            intlKey: 'translate.system.message.chat.offload.failed'
          })
        );
        break;

      // Chat Transfer
      case EXTERNAL_APP_EVENTS.TRANSFER_START:
        yield put(
          createSystemMessage({
            type: SYSTEM_MESSAGE_TYPES.INSTRUCTION,
            intlKey: 'translate.system.message.chat.transfer.start',
            intlValues: {
              firstName: clusterAction.payload.operator.first_name,
              lastName: clusterAction.payload.operator.last_name
            }
          })
        );
        break;
      case EXTERNAL_APP_EVENTS.TRANSFER_COMPLETED:
        yield put(FromClusterActions.transferCompleted({ ...clusterAction.payload }));
        break;
      case EXTERNAL_APP_EVENTS.TRANSFER_FAILED:
        yield put(
          createSystemMessage({
            type: SYSTEM_MESSAGE_TYPES.INSTRUCTION,
            intlKey: 'translate.system.message.chat.transfer.failed'
          })
        );
        break;
      case EXTERNAL_APP_EVENTS.TRANSFER_TIMEOUT:
        yield put(
          createSystemMessage({
            type: SYSTEM_MESSAGE_TYPES.INSTRUCTION,
            intlKey: 'translate.system.message.chat.transfer.timeout'
          })
        );
        break;
      case EXTERNAL_APP_EVENTS.TRANSFER_DECLINED:
        yield put(
          createSystemMessage({
            type: SYSTEM_MESSAGE_TYPES.INSTRUCTION,
            intlKey: 'translate.system.message.chat.transfer.decline'
          })
        );
        break;
      case WEBHOOK_EVENTS.LOGOUT_USER_FROM_TOOLS:
        trigger(EVENTS.LOGOUT_FROM_SITE_TOOLS);
        break;
      case WEBHOOK_EVENTS.LOGOUT_USER_FROM_UA:
        trigger(EVENTS.LOGOUT_USER_FROM_UA);
        break;
      default:
        break;
    }
  }
}

function* onChatEnd() {
  const { chat } = yield select();

  if (!chat.feedbackSent) {
    yield put(push(ROUTES.CHAT_END));
  }
}

function* onChatNotExists({ payload }) {
  const lostOperatorConnection = yield select(didOperatorLostConnection);
  const chatEndedByUser = yield select(isChatEndedByUser);
  const pathname = yield select(getCurrentPathname);
  const reason = payload.reason;

  const shouldCloseChatWindow = Boolean(
    !lostOperatorConnection && !chatEndedByUser && reason !== CHAT_NOT_EXISTS_REASONS.OPERATOR_ENDED_CHAT
  );

  // TODO this is a temp fix form SPA-2703 until DMT-31139 is ready
  const operateLostConnectionOnWelcomeScreen =
    reason === CHAT_NOT_EXISTS_REASONS.OPERATOR_LOST_CONNECTION &&
    (pathname === ROUTES.WAITING_FOR_OPERATOR || pathname === ROUTES.ROOT);

  if (shouldCloseChatWindow || operateLostConnectionOnWelcomeScreen) {
    // when reason is OPERATOR_ENDED_CHAT, user should be able to leave a review
    return yield call(closeChatWindowSaga);
  }

  yield call(onChatEnd);
}

export function* handleChatWindowNavigation(action) {
  yield fork(waitForChatRender, {
    onRendered: function* () {
      const chatState = yield select(getChatState);
      if (chatState === CHAT_STATE.REQUESTING_CHAT) {
        return yield put(push(ROUTES.WAITING_FOR_OPERATOR));
      }

      return yield put(push(ROUTES.CHAT));
    }
  });

  const isChatRendered = Boolean(document.getElementById(CHAT_LAYOUT_DOM_ID));

  if (!isChatRendered) {
    trigger(EVENTS.CHAT_STARTED);
  }
}

export default function* () {
  yield takeLatest(FromClusterActions.CHAT_CLOSED_BY_OPERATOR, onChatEnd);
  yield takeLatest(FromClusterActions.CHAT_CLOSED_BY_ADMIN, onChatEnd);

  yield takeLatest(FromClusterActions.CHAT_NOT_EXISTS, onChatNotExists);

  yield takeEvery([FromClusterActions.CHAT_ASSIGNED, FromClusterActions.CONTINUE_CHAT], handleChatWindowNavigation);

  let detectedMessagingErrorSent = false;
  yield takeEvery(FromClusterActions.OPERATOR_SEND_MESSAGE, function* () {
    const state = yield select(getChatState);

    const shouldLogError = Boolean(state === CHAT_STATE.REQUESTING_CHAT);

    if (shouldLogError && !detectedMessagingErrorSent) {
      detectedMessagingErrorSent = true;

      const debugEvents = yield select(getDebugEvents);

      return sendLog({
        error: 'Chat-Error --->>> User receives messages, but chat is in requesting state',
        errorInfo: {
          componentStack: ''
        },
        data: JSON.stringify({
          agent: window.navigator.userAgent,
          chatState: state,
          debugEvents
        })
      });
    }
    if (window.document.visibilityState === 'hidden') {
      return yield put(FromClientFEActions.userUnseenChatMessages());
    }
  });

  yield takeEvery(FromClusterActions.USER_SUBMITTED_CHAT_RATING, closeChatWindowSaga);
}
